/**
 * Stuff that didn't fit anywhere else
 * */

import { CLOUDFLARE_IMAGE_PROXY } from "./constants";

export class UnreachableError extends Error {
  constructor(message: string) {
    super(`Unreachable code reached: ${message}`);
  }
}

export function assertUnreachable(_x: never): never {
  throw new UnreachableError(`Unreachable code reached`);
}

export function validateIsRecord(
  thing: unknown,
): thing is Record<string, unknown> {
  return (
    typeof thing === "object" &&
    thing !== null &&
    !Array.isArray(thing) &&
    !(thing instanceof Date)
  );
}

export function isError(value: unknown): value is Error {
  return (
    value instanceof Error || // Standard Error object
    (typeof value === "object" && value !== null && "message" in value) // Compatible objects
  );
}

type CloudflareImageOptions = {
  width: number;
  height?: number;
  quality?: number;
  format?: string;
  fit?: "cover" | "contain" | "scale-down" | "crop" | "pad";
  /** "none" applies any colour profile and then drops it, along with EXIF and XMP. */
  metadata?: "none" | "copyright" | "keep";
};

/**
 * Builds a Cloudflare image URL with the given options.
 * @param imageUrl - The URL of the image to optimize.
 * @param options - The Cloudflare image optimization options.
 * @returns The optimized image URL.
 * @example
 * const url = buildCloudflareImageUrl("https://example.com/image.jpg", { width: 100 });
 */
export function buildCloudflareImageUrl(
  imageUrl: string,
  options: Partial<CloudflareImageOptions> = {},
) {
  let cloudflareUrl = CLOUDFLARE_IMAGE_PROXY;

  const optionsWithDefaults = {
    format: "auto",
    fit: "cover",
    ...options,
  };

  for (const [key, v] of Object.entries(optionsWithDefaults)) {
    cloudflareUrl += `${key}=${encodeURIComponent(v)}%2C`;
  }

  return cloudflareUrl + `/${encodeURIComponent(imageUrl)}`;
}

/** Proxied as-is so crawlers can index it: the asset CDN sends `X-Robots-Tag: noindex`. */
export function buildIndexableImageUrl(imageUrl: string): string {
  return imageUrl.startsWith("https://")
    ? `${CLOUDFLARE_IMAGE_PROXY}fit=scale-down/${encodeURIComponent(imageUrl)}`
    : imageUrl;
}
